/**
 * `/audiences/fields` — the field catalogue, under Audiences beside the
 * uploads it is computed from (slice 67).
 *
 * This file owns the reads and writes; `FieldsContent` draws them. Every write
 * re-reads the catalogue afterwards rather than patching it locally, because a
 * write here changes more than its own row: correcting a column's type changes
 * whether rules may use it, answering a date format clears `needsDateFormat`
 * and can retire a suggestion, and adding a derived field adds a row to another
 * group and removes the "Age from …" chip it came from. The catalogue is
 * computed on read, so asking again is the one answer that cannot drift.
 *
 * The re-read keeps what is on screen while it runs (`useAsyncData` keeps the
 * previous data during a reload), so the row a person just edited does not
 * blink out and back.
 */
import type { CreateDerivedFieldRequest, UpdateFieldSettingsRequest } from '@wend/shared';
import { Card, CardContent, Spinner, buttonVariants, useToast } from '@wend/ui';
import { ArrowLeft } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { AppLayout } from '../../../components/AppLayout';
import { ErrorState } from '../../../components/ErrorState';
import { FormAlert } from '../../../components/FormAlert';
import { errorMessage } from '../../../lib/api/errors';
import { createDerivedField, deleteDerivedField, fetchFieldCatalogue, updateFieldSettings } from '../../../lib/api/fields';
import { useAsyncData } from '../../../lib/use-async-data';
import { FieldsContent, type FieldActions } from './FieldsContent';

export function FieldsPage() {
  const { toast } = useToast();
  const { data, error, loadingFirst, refreshing, reload } = useAsyncData((signal) => fetchFieldCatalogue(signal), []);

  const actions = useMemo<FieldActions>(
    () => ({
      saveSettings: async (key: string, request: UpdateFieldSettingsRequest) => {
        await updateFieldSettings(key, request);
        toast({ tone: 'success', title: 'Field updated' });
        reload();
      },
      createDerived: async (request: CreateDerivedFieldRequest) => {
        const created = await createDerivedField(request);
        toast({ tone: 'success', title: 'Field added', description: `${created.label} is ready to use in rules.` });
        reload();
      },
      deleteDerived: async (derivedFieldId: string) => {
        await deleteDerivedField(derivedFieldId);
        toast({ tone: 'success', title: 'Field deleted' });
        reload();
      },
    }),
    [reload, toast],
  );

  return (
    <AppLayout
      title="Fields"
      actions={
        <Link to="/audiences" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
          <ArrowLeft aria-hidden="true" className="size-4" />
          All lists
        </Link>
      }
    >
      {data === null ? (
        <Card>
          {loadingFirst ? (
            <CardContent className="flex items-center gap-3 px-6 py-12 text-[0.92rem] text-muted">
              <Spinner size="md" label={null} />
              <span role="status">Reading every column of every upload…</span>
            </CardContent>
          ) : (
            <ErrorState error={error} title="Could not load your fields" onRetry={reload} />
          )}
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {error !== null && (
            <FormAlert tone="error">{errorMessage(error)} What is shown below is the last version that loaded.</FormAlert>
          )}
          {refreshing && (
            <p className="flex items-center gap-2 text-[0.82rem] text-muted" role="status">
              <Spinner size="sm" label={null} />
              Updating the fields
            </p>
          )}
          <FieldsContent catalogue={data} actions={actions} />
        </div>
      )}
    </AppLayout>
  );
}
